import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Phone, Mail, ShieldCheck, Car, MessageSquare,
  Star, Copy, ExternalLink, CheckCircle2, Loader2, CalendarDays, Info,
  Image as ImageIcon, ChevronDown, ChevronUp, Globe,
} from "lucide-react";
import MapDisplay from "../components/MapDisplay";
import { PublicVehicleGallery, VehicleCoverThumb } from "../components/PublicFleetPhotos";
import DemoBadge from "../components/DemoBadge";
import PublicVehicleDetailModal from "../components/minisite/PublicVehicleDetailModal";
import TrustBadges from "../components/TrustBadges";
import PublicLegalTrust from "../components/client-legal/PublicLegalTrust";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function RentalProfile({ preloaded = null, onVehicleModalOpenChange = null } = {}) {
  const { id, slug } = useParams();
  const key = preloaded ? null : (slug || id);
  const [data, setData] = useState(preloaded);
  const [loading, setLoading] = useState(!preloaded);
  const [showInquiry, setShowInquiry] = useState(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/public/rentals/${key}`);
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    if (preloaded) return;
    if (!key) return;
    let cancelled = false;
    (async () => { if (!cancelled) await fetchProfile(); })();
    return () => { cancelled = true; };
  }, [key, preloaded, fetchProfile]);

  if (loading) {
    return <div className="max-w-5xl mx-auto px-4 py-20 flex items-center justify-center text-slate-500" data-testid="rental-profile-loading">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
    </div>;
  }
  if (!data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="rental-profile-404">
        <h2 className="font-display text-2xl font-semibold">Business not found</h2>
        <p className="mt-2 text-sm text-slate-600">This business may have hidden its public profile or doesn&apos;t exist.</p>
        <Link to="/rentals" className="mt-4 inline-flex items-center gap-1 text-[#1B4332] underline text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to Discover Rentals
        </Link>
      </div>
    );
  }

  const c = data;
  const logoUrl = c.public_logo_url;
  const ms = c.is_enterprise ? c.mini_site : null;
  const sections = ms?.sections_enabled || {};
  const theme = ms?.theme_color || "#1B4332";
  
  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem("confirmph_token");
  const serviceType = c.service_type || c.business_type || "";
  const isCarMotor = serviceType.includes("car") || serviceType.includes("motor");
  
  return (
    <div data-testid="rental-profile-page">{ms && (
        <MiniSiteHero company={c} ms={ms} theme={theme} logoUrl={logoUrl}
                       enabled={sections.hero !== false} />
      )}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 flex items-center justify-between gap-3">
        <Link to="/rentals" data-testid="rp-back-link" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> All rentals
        </Link>
        {c.is_enterprise && c.public_website_enabled !== false && (
          <Link
            to={`/rentals/${c.public_slug || c.id}`}
            data-testid="rp-view-website-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg btn-brand text-xs sm:text-sm font-medium"
          >
            <Globe className="w-3.5 h-3.5" /> View website
          </Link>
        )}
      </div>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">

      {/* Coming Soon Banner & Signup Gate */}
      {isCarMotor && !isLoggedIn && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  🚗 Car & Motor Rentals Available Now
                </h3>
                <p className="text-sm text-gray-600">
                  Other rental services coming soon! Sign up to get notified and access exclusive deals.
                </p>
              </div>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium whitespace-nowrap"
              >
                Sign Up Free
                <ArrowLeft className="w-4 h-4 rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      )}

        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <div className="flex flex-wrap items-start gap-4">
            {logoUrl && (
              <img src={logoUrl} alt={`${c.name} logo`} data-testid="rp-logo"
                    className="w-20 h-20 rounded-xl border border-slate-200 object-cover flex-shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-3xl sm:text-4xl font-bold">{c.name}</h1>
                {c.is_demo && <DemoBadge variant="default" size="sm" testId="rp-demo-badge" />}
                {c.online_booking_enabled && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="rp-online-badge">
                    {c.booking_mode === "automatic_hold" ? "Online booking" : "Booking requests"}
                  </span>
                )}
              </div>
              <div className="mt-1 text-sm text-slate-600 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> {c.city || "-"} · {c.business_type?.replace("_", " ")}
              </div>
              {c.rating_avg != null && (
                <div className="mt-2 flex items-center gap-1 text-amber-700 font-semibold" data-testid="rp-rating">
                  <Star className="w-4 h-4 fill-amber-500 stroke-amber-500" />
                  {c.rating_avg.toFixed(1)} <span className="text-xs font-normal text-slate-600">({c.rating_count} booking-linked review{c.rating_count === 1 ? "" : "s"})</span>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {c.online_booking_enabled && (
                <Link
                  to={`/business/${c.id}`}
                  data-testid="rp-book-online"
                  className="px-4 py-2 rounded-lg btn-brand font-medium text-sm inline-flex items-center gap-1"
                >
                  {c.booking_mode === "automatic_hold" ? "Book online" : "Request booking"}
                </Link>
              )}
              <button
                onClick={() => setShowInquiry((s) => !s)}
                data-testid="rp-contact-toggle"
                className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center gap-1"
              >
                <MessageSquare className="w-3.5 h-3.5" /> Send inquiry
              </button>
              <Link
                to="/find-booking"
                data-testid="rp-find-my-booking"
                className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm"
              >
                Find my booking
              </Link>
            </div>
          </div>

          {c.public_description_full && (
            <p className="mt-5 text-slate-700 leading-relaxed whitespace-pre-line">{c.public_description_full}</p>
          )}
        </div>
      </section>

      {showInquiry && (
        <InquirySection id={c.id} businessName={c.name} onClose={() => setShowInquiry(false)} />
      )}

      {(c.about_images || []).length > 0 && (
        <AboutGallery companyId={c.id} images={c.about_images} />
      )}

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-2 grid lg:grid-cols-3 gap-4">
        <ContactCard contact={c.contact} />
        <BookingStatusCard card={c} />
        <PickupCard locations={c.public_pickup_locations} />
      </section>

      <TrustBadges companyId={c.id} />
      <PublicLegalTrust trust={c.legal_trust} />

      {c.business_location && (c.business_location.has_coordinates || c.business_location.city || c.business_location.label) && (
        <BusinessLocationSection location={c.business_location} />
      )}

      {(c.vehicles || []).length > 0 && (
        <section id="vehicles" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h2 className="font-display text-2xl font-semibold">Vehicles</h2>
          <p className="text-sm text-slate-500 mb-4">All listed vehicles below are active in the fleet.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rp-vehicles">
            {c.vehicles.map((v) => (
              <PublicVehicleCard key={v.id} companyId={c.id} vehicle={v} onModalOpenChange={onVehicleModalOpenChange} />
            ))}
          </div>
        </section>
      )}

      {c.public_terms && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="font-display text-xl font-semibold">Rental terms & conditions</h2>
            <p className="mt-2 text-sm text-slate-700 whitespace-pre-line">{c.public_terms}</p>
            <p className="mt-3 text-xs text-slate-500">Always review booking details, prices, the advance and security-deposit rules, who may drive, and pickup arrangements before confirming.</p>
          </div>
        </section>
      )}

      <ReviewsSection reviews={c.reviews || []} avg={c.rating_avg} count={c.rating_count} />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div>
              <h3 className="font-display font-semibold">This business uses ConfirmPH</h3>
              <p className="mt-1 text-sm text-emerald-900/90">
                ConfirmPH helps record bookings, send confirmation links, and reduce booking
                misunderstandings. ConfirmPH does not own or operate the rental businesses listed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {ms && (
        <>
          <MiniSiteAbout ms={ms} theme={theme} enabled={sections.about !== false} />
          <MiniSiteHowItWorks theme={theme} enabled={sections.how_it_works !== false} />
          {ms.show_policies !== false && <MiniSitePolicies ms={ms} theme={theme} enabled={sections.policies !== false} />}
          {ms.show_faq !== false && <MiniSiteFAQ ms={ms} theme={theme} enabled={sections.faq !== false} />}
          <MiniSiteTrust theme={theme} enabled={sections.trust !== false} />
        </>
      )}
    </div>
  );
}

function ContactCard({ contact }) {
  const hasAny = contact?.phone || contact?.email || contact?.facebook_url || contact?.messenger_url || contact?.whatsapp;
  if (!hasAny) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="rp-contact-card">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Contact</div>
        <p className="mt-2 text-sm text-slate-600">This business hasn&apos;t published direct contact info. Use the inquiry form above.</p>
      </div>
    );
  }
  const copy = (label, v) => async () => {
    try { await navigator.clipboard.writeText(v); toast.success(`${label} copied`, { duration: 1500 }); }
    catch { toast.error("Could not copy"); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-2" data-testid="rp-contact-card">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Contact</div>
      {contact.phone && (
        <Row icon={Phone} label="Phone" value={contact.phone} onCopy={copy("Phone", contact.phone)} testId="rp-contact-phone" />
      )}
      {contact.email && (
        <Row icon={Mail} label="Email" value={contact.email} onCopy={copy("Email", contact.email)} testId="rp-contact-email" />
      )}
      {contact.whatsapp && (
        <Row icon={MessageSquare} label="WhatsApp" value={contact.whatsapp} onCopy={copy("WhatsApp", contact.whatsapp)} testId="rp-contact-whatsapp" />
      )}
      {contact.facebook_url && (
        <LinkRow label="Facebook" url={contact.facebook_url} testId="rp-contact-facebook" />
      )}
      {contact.messenger_url && (
        <LinkRow label="Messenger" url={contact.messenger_url} testId="rp-contact-messenger" />
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, onCopy, testId }) {
  return (
    <button onClick={onCopy} type="button" data-testid={testId} className="w-full flex items-center justify-between gap-2 text-left rounded-lg px-2 py-1.5 hover:bg-slate-50">
      <span className="flex items-center gap-2 text-sm min-w-0">
        <Icon className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
        <span className="font-mono truncate">{value}</span>
      </span>
      <Copy className="w-3 h-3 text-slate-400" />
    </button>
  );
}
function LinkRow({ label, url, testId }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={testId} className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50 text-sm">
      <span className="flex items-center gap-2"><ExternalLink className="w-3.5 h-3.5 text-slate-500" /> {label}</span>
      <span className="text-xs text-slate-500 truncate max-w-[55%]">{url.replace("https://", "")}</span>
    </a>
  );
}

function BookingStatusCard({ card }) {
  let lines = [];
  if (card.online_booking_enabled && card.booking_mode === "automatic_hold") {
    lines = ["Online booking available", "Vehicle held while you confirm details", "Real provider payments still respect your plan setup"];
  } else if (card.online_booking_enabled && card.booking_mode === "request_only") {
    lines = ["Booking requests accepted", "Staff confirms the request before any hold", "No charge until the business confirms"];
  } else {
    lines = ["Contact only", "Use the inquiry form or contact info on the left", "Verify any booking via Find My Booking once received"];
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="rp-booking-status">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Booking</div>
      <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
        {lines.map((l, i) => (
          <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-700 flex-shrink-0" />{l}</li>
        ))}
      </ul>
    </div>
  );
}

function PickupCard({ locations }) {
  if (!locations?.length) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="rp-pickup-card">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Pickup</div>
        <p className="mt-2 text-sm text-slate-600">Confirm pickup location with the business before payment.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="rp-pickup-card">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Pickup options</div>
      <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
        {locations.map((l, i) => (
          <li key={i} className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-0.5 text-slate-500 flex-shrink-0" />{l}</li>
        ))}
      </ul>
    </div>
  );
}

function ReviewsSection({ reviews, avg, count }) {
  if (!count) {
    return (
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600" data-testid="rp-reviews-empty">
          No booking-linked reviews yet. Reviews here are only published for completed bookings on ConfirmPH.
        </div>
      </section>
    );
  }
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <h2 className="font-display text-2xl font-semibold">Reviews</h2>
        <div className="text-sm text-slate-600">
          <span className="text-amber-700 font-semibold">★ {avg.toFixed(1)}</span> · {count} booking-linked
        </div>
      </div>
      <div className="mt-3 space-y-3" data-testid="rp-reviews">
        {reviews.map((r) => (
          <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4"
               data-testid={`rp-review-${r.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-medium">{r.customer_name_display}</div>
              <div className="flex items-center gap-1 text-amber-700 font-semibold text-sm">
                <Star className="w-3.5 h-3.5 fill-amber-500 stroke-amber-500" /> {r.rating}/5
              </div>
            </div>
            {r.title && <div className="mt-1 font-semibold">{r.title}</div>}
            <p className="mt-1.5 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{r.comment}</p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {r.verified !== false && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200"
                      data-testid={`rp-review-verified-${r.id}`}>
                  Booking-linked
                </span>
              )}
              <span className="text-[10px] text-slate-500">{r.created_at?.slice(0, 10)}</span>
            </div>
            {r.business_reply && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5"
                   data-testid={`rp-review-reply-${r.id}`}>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Business reply
                </div>
                <p className="mt-1 text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                  {r.business_reply}
                </p>
                {r.business_reply_at && (
                  <div className="text-[10px] text-slate-400 mt-1">
                    {r.business_reply_at.slice(0, 10)}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function InquirySection({ id, businessName, onClose }) {
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    preferred_vehicle: "", start_date: "", end_date: "",
    pickup_location: "", notes: "", consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please tick the consent box to send your inquiry.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/rentals/${id}/inquiry`, form);
      setDone(true);
      toast.success("Inquiry sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your inquiry");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="rp-inquiry-done">
          <h3 className="font-display text-lg font-semibold text-emerald-900">Inquiry sent to {businessName}</h3>
          <p className="text-sm text-emerald-900/90 mt-1">
            They&apos;ve received the details and will get back to you soon. If they don&apos;t respond, try the contact info below.
          </p>
          <button onClick={onClose} className="mt-3 text-sm underline">Close</button>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
      <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3" data-testid="rp-inquiry-form">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Send booking inquiry to {businessName}</h3>
          <button type="button" onClick={onClose} className="text-sm text-slate-500 hover:text-slate-900">Cancel</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Your name" required value={form.customer_name} testId="rp-inq-name"
                 onChange={(v) => setForm({ ...form, customer_name: v })} />
          <Field label="Email" type="email" required value={form.customer_email} testId="rp-inq-email"
                 onChange={(v) => setForm({ ...form, customer_email: v })} />
          <Field label="Phone (optional)" value={form.customer_phone} testId="rp-inq-phone"
                 onChange={(v) => setForm({ ...form, customer_phone: v })} />
          <Field label="Preferred vehicle / type" value={form.preferred_vehicle} testId="rp-inq-vehicle"
                 onChange={(v) => setForm({ ...form, preferred_vehicle: v })} />
          <Field label="Pickup / start" type="date" value={form.start_date} testId="rp-inq-start"
                 onChange={(v) => setForm({ ...form, start_date: v })} />
          <Field label="Return / end" type="date" value={form.end_date} testId="rp-inq-end"
                 onChange={(v) => setForm({ ...form, end_date: v })} />
          <Field label="Pickup location (optional)" value={form.pickup_location} testId="rp-inq-pickup"
                 onChange={(v) => setForm({ ...form, pickup_location: v })} className="sm:col-span-2" />
          <Textarea label="Notes (optional)" value={form.notes} testId="rp-inq-notes"
                     onChange={(v) => setForm({ ...form, notes: v })} className="sm:col-span-2" />
        </div>
        <label className="flex items-start gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={form.consent} data-testid="rp-inq-consent"
                  onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5" />
          <span>
            I understand these details are provided to {businessName} through ConfirmPH so it can
            respond to this inquiry, and are processed as described in the{" "}
            <a href="/privacy" className="underline">Privacy Policy</a>. An inquiry is not a
            confirmed rental.
          </span>
        </label>
        <button type="submit" disabled={submitting} data-testid="rp-inq-submit"
                 className="px-4 py-2 rounded-lg btn-brand font-medium text-sm inline-flex items-center gap-1 disabled:opacity-60">
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
          Send inquiry
        </button>
      </form>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", required, className = "", testId, ...rest }) {
  return (
    <label className={`flex flex-col gap-1 text-xs text-slate-600 ${className}`}>
      <span className="font-medium">{label}</span>
      <input type={type} required={required} data-testid={testId}
              value={value} onChange={(e) => onChange(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-300 bg-white focus:border-[#1B4332] focus:ring-2 focus:ring-[#1B4332]/15 outline-none"
              {...rest} />
    </label>
  );
}

function Textarea({ label, value, onChange, className = "", testId }) {
  return (
    <label className={`flex flex-col gap-1 text-xs text-slate-600 ${className}`}>
      <span className="font-medium">{label}</span>
      <textarea data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
                 rows={3}
                 className="px-3 py-2 rounded-lg border border-slate-300 bg-white focus:border-[#1B4332] focus:ring-2 focus:ring-[#1B4332]/15 outline-none" />
    </label>
  );
}

function AboutGallery({ companyId, images }) {
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-4" data-testid="rp-about-gallery">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
        {images.map((img) => <AboutThumb key={img.id} companyId={companyId} id={img.id} />)}
      </div>
    </section>
  );
}

function AboutThumb({ companyId, id }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/public/rentals/${companyId}/photos/${id}`)
      .then((r) => { if (alive) setSrc(r.data.data_url); })
      .catch(() => {});
    return () => { alive = false; };
  }, [companyId, id]);
  return (
    <div className="aspect-[4/3] rounded-lg overflow-hidden border border-slate-200 bg-slate-100 grid place-items-center" data-testid={`rp-about-${id}`}>
      {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-slate-300" />}
    </div>
  );
}


function BusinessLocationSection({ location }) {
  const hasMap = location.has_coordinates && location.lat != null && location.lng != null;
  if (!hasMap) {
    // Fallback: just show the city/service-area card (no map)
    return (
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-testid="rp-location-section">
        <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3">
          <MapPin className="w-5 h-5 text-[#1B4332] mt-0.5" />
          <div>
            <div className="font-semibold text-slate-900">Service area</div>
            <div className="text-sm text-slate-700 mt-0.5">{location.label || location.city || "Location not shared publicly"}</div>
            {location.service_area_text && (
              <div className="mt-1 text-xs text-slate-500">{location.service_area_text}</div>
            )}
            <div className="mt-2 text-xs text-slate-500">Exact pickup location is shared after booking.</div>
          </div>
        </div>
      </section>
    );
  }
  const approximate = location.mode === "approximate";
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="rp-location-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#1B4332]" />
          <h2 className="font-display text-xl font-semibold">{approximate ? "Service area" : "Pickup location"}</h2>
        </div>
        {approximate && (
          <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200" data-testid="rp-location-approx-badge">
            Approximate area: exact pickup shared after booking
          </span>
        )}
      </div>
      <MapDisplay
        lat={location.lat}
        lng={location.lng}
        label={location.label || location.city}
        approximate={approximate}
        height="320px"
      />
      <div className="mt-3 text-sm text-slate-700">
        {location.label || location.city}
        {location.address && (
          <span className="block text-xs text-slate-500 mt-0.5">{location.address}</span>
        )}
        {location.service_area_text && (
          <span className="block text-xs text-slate-500 mt-0.5">{location.service_area_text}</span>
        )}
      </div>
    </section>
  );
}


function PublicVehicleCard({ companyId, vehicle: v, onModalOpenChange = null }) {
  const [showGallery, setShowGallery] = useState(false);
  const [showDetail, setShowDetailState] = useState(false);
  const hasPhotos = (v.public_photos_count || 0) > 0;
  function setShowDetail(value) {
    setShowDetailState(value);
    onModalOpenChange?.(value);
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col" data-testid={`rp-vehicle-${v.id}`}>
      <VehicleCoverThumb companyId={companyId} vehicleId={v.id} coverId={v.cover_photo_id} />
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center gap-2">
          <Car className="w-4 h-4 text-slate-500" />
          <div className="font-medium truncate">{v.name}</div>
        </div>
        <div className="mt-1 text-xs text-slate-500">
          {[v.category, v.transmission, v.seats && `${v.seats} seats`, v.fuel_type, v.engine_cc && `${v.engine_cc}cc`]
            .filter(Boolean).join(" · ")}
        </div>
        {(v.service_modes?.self_drive || v.service_modes?.with_driver) && (
          <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`rp-vehicle-modes-${v.id}`}>
            {v.service_modes.self_drive && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700">
                Self-drive
              </span>
            )}
            {v.service_modes.with_driver && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700">
                With driver
              </span>
            )}
          </div>
        )}
        {v.public_description && (
          <p className="mt-2 text-xs text-slate-600 line-clamp-2">{v.public_description}</p>
        )}
        {v.price_per_day != null && (
          <div className="mt-2 text-sm font-semibold text-slate-900">
            ₱{Number(v.price_per_day).toLocaleString()}
            <span className="text-xs font-normal text-slate-500"> / day</span>
            {v.deposit_amount != null && (
              <span className="ml-2 text-xs font-normal text-slate-500">
                · Security deposit ₱{Number(v.deposit_amount).toLocaleString()}
              </span>
            )}
          </div>
        )}
        <div className="mt-3 flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setShowDetail(true)}
            data-testid={`rp-vehicle-view-details-${v.id}`}
            className="text-xs font-medium px-3 py-1.5 rounded-md btn-brand inline-flex items-center gap-1"
          >
            View details →
          </button>
          {hasPhotos && (
            <button
              type="button"
              onClick={() => setShowGallery((s) => !s)}
              data-testid={`rp-vehicle-toggle-gallery-${v.id}`}
              className="text-xs font-medium text-[#1B4332] hover:underline inline-flex items-center gap-1"
            >
              <ImageIcon className="w-3.5 h-3.5" />
              {showGallery ? "Hide photos" : `View ${v.public_photos_count} photo${v.public_photos_count === 1 ? "" : "s"}`}
              {showGallery ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
        {showGallery && (
          <PublicVehicleGallery companyId={companyId} vehicleId={v.id} />
        )}
      </div>
      {showDetail && (
        <PublicVehicleDetailModal companyId={companyId} vehicle={v} onClose={() => setShowDetail(false)} />
      )}
    </div>
  );
}



function MiniSiteHero({ company, ms, theme, logoUrl, enabled }) {
  if (!enabled) return null;
  const heroBg = ms.hero_image_url || company.public_cover_image_url;
  // Plan-aware CTA: if the business cannot accept in-app bookings (Free/Basic,
  // or Pro/Enterprise with the toggle off), force inquiry-only copy
  // regardless of the static mini-site `main_cta_type` config.
  const canBook = !!company.online_booking_enabled;
  const ctaLabel = !canBook ? "Send inquiry"
    : ms.main_cta_type === "send_inquiry" ? "Send inquiry"
    : ms.main_cta_type === "book_now" ? "Book now"
    : "Request booking";
  return (
    <section className="relative overflow-hidden" data-testid="ms-hero"
             style={{ background: `linear-gradient(135deg, ${theme}15, ${theme}05)` }}>
      {heroBg && (
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(135deg, ${theme}cc 0%, ${theme}55 60%, rgba(255,255,255,0.15) 100%), url('${heroBg}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      )}
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          {logoUrl && (
            <img src={logoUrl} alt="" className="w-20 h-20 rounded-xl object-cover border-2 border-white shadow flex-shrink-0"
                 data-testid="ms-hero-logo" />
          )}
          <div className="flex-1">
            {company.is_demo && (
              <div className="mb-2">
                <DemoBadge variant="ribbon" size="md" testId="ms-hero-demo-badge" />
              </div>
            )}
            <div className={`text-xs font-semibold uppercase tracking-[0.2em] ${heroBg ? "text-white/90" : ""}`}
                 style={heroBg ? undefined : { color: theme }}>{company.city || "Philippines"} · {(company.service_type || "rental").replace("_", " ")}</div>
            <h1 className={`font-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-1 ${heroBg ? "text-white drop-shadow" : ""}`}
                style={heroBg ? undefined : { color: theme }} data-testid="ms-hero-name">{company.name}</h1>
            {ms.headline && (
              <p className={`text-lg mt-2 ${heroBg ? "text-white/95" : "text-slate-800"}`} data-testid="ms-hero-headline">
                {ms.headline}
              </p>
            )}
            {ms.intro && (
              <p className={`text-sm mt-1 max-w-2xl ${heroBg ? "text-white/90" : "text-slate-700"}`} data-testid="ms-hero-intro">
                {ms.intro}
              </p>
            )}
            <div className="flex flex-wrap gap-2 mt-4">
              <a href="#book" data-testid="ms-cta-book"
                 className="px-5 py-2.5 rounded-lg text-white font-medium text-sm shadow"
                 style={{ background: theme }}>
                {ctaLabel}
              </a>
              <a href="#vehicles" data-testid="ms-cta-vehicles"
                 className="px-5 py-2.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium text-slate-800">
                View vehicles
              </a>
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-600">
              <ShieldCheck className="w-3.5 h-3.5" /> Booking website powered by ConfirmPH
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MiniSiteAbout({ ms, theme, enabled }) {
  if (!enabled || !(ms.about || ms.why_choose_us || ms.service_area_text)) return null;
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
             data-testid="ms-about">
      <h2 className="font-display text-2xl font-semibold" style={{ color: theme }}>About us</h2>
      {ms.about && <p className="text-slate-700 mt-2 whitespace-pre-line">{ms.about}</p>}
      {ms.why_choose_us && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Why choose us</div>
          <p className="text-slate-700 mt-1 whitespace-pre-line">{ms.why_choose_us}</p>
        </div>
      )}
      {(ms.service_area_text || ms.pickup_delivery_notes) && (
        <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
          {ms.service_area_text && (
            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="ms-service-area">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Service area</div>
              <p className="text-slate-700 mt-0.5">{ms.service_area_text}</p>
            </div>
          )}
          {ms.pickup_delivery_notes && (
            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="ms-pickup-delivery">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Pickup & delivery</div>
              <p className="text-slate-700 mt-0.5">{ms.pickup_delivery_notes}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function MiniSiteHowItWorks({ theme, enabled }) {
  if (!enabled) return null;
  const steps = [
    "Choose a rental that fits your dates and needs.",
    "Send a request or book directly when enabled.",
    "The business reviews your request and confirms.",
    "You receive a ConfirmPH confirmation by email.",
    "Pickup and inspect the rental together at handover.",
    "Return the rental and close the booking.",
  ];
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="ms-how">
      <h2 className="font-display text-2xl font-semibold" style={{ color: theme }}>How booking works</h2>
      <ol className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {steps.map((s, i) => (
          <li key={i} className="rounded-lg border border-slate-200 bg-white p-3 flex gap-2"
              data-testid={`ms-how-step-${i+1}`}>
            <span className="w-6 h-6 rounded-full text-white text-xs grid place-items-center flex-shrink-0"
                  style={{ background: theme }}>{i + 1}</span>
            <span className="text-sm text-slate-700">{s}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function MiniSitePolicies({ ms, theme, enabled }) {
  if (!enabled || !ms.policies || Object.keys(ms.policies).length === 0) return null;
  const LABELS = {
    deposit: "Security deposit", advance: "Advance / downpayment",
    cancellation: "Cancellation", late_return: "Late return",
    fuel: "Fuel", damage: "Damage", cleaning: "Cleaning",
    extension: "Extension", delivery: "Delivery / Pickup",
    travel: "Travel area", refund: "Refund",
    authorized_drivers: "Authorized drivers", driver_service: "Driver service",
    insurance: "Insurance / excess", mileage: "Mileage",
    tolls: "Traffic fines & tolls", accidents: "Accidents & breakdowns",
    post_rental_charges: "Post-rental charges",
  };
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="ms-policies">
      <h2 className="font-display text-2xl font-semibold" style={{ color: theme }}>Policies</h2>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        {Object.entries(ms.policies).map(([k, v]) => (
          v ? (
            <div key={k} className="rounded-lg border border-slate-200 bg-white p-3"
                 data-testid={`ms-policy-${k}`}>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{LABELS[k] || k}</div>
              <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-line">{v}</p>
            </div>
          ) : null
        ))}
      </div>
      <p className="text-[11px] text-slate-500 mt-2 italic">
        Policies are set by the business. ConfirmPH provides tools to display and organize them but the business remains responsible for their own terms.
        Damage found at return is assessed under those terms and the available evidence: it does not by itself establish who is responsible or how much is owed, and acknowledging that damage exists is not agreeing to a claimed amount.
      </p>
    </section>
  );
}

function MiniSiteFAQ({ ms, theme, enabled }) {
  const [openId, setOpenId] = useState(null);
  if (!enabled || !ms.faqs || ms.faqs.length === 0) return null;
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="ms-faq">
      <h2 className="font-display text-2xl font-semibold" style={{ color: theme }}>Frequently asked questions</h2>
      <ul className="mt-3 divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
        {ms.faqs.map((f) => (
          <li key={f.id} className="p-3" data-testid={`ms-faq-${f.id}`}>
            <button onClick={() => setOpenId(openId === f.id ? null : f.id)}
                    className="w-full text-left font-medium text-slate-900 flex justify-between gap-2"
                    data-testid={`ms-faq-toggle-${f.id}`}>
              <span>{f.question}</span>
              <span className="text-slate-400">{openId === f.id ? "−" : "+"}</span>
            </button>
            {openId === f.id && (
              <p className="text-sm text-slate-700 mt-1 whitespace-pre-line" data-testid={`ms-faq-answer-${f.id}`}>
                {f.answer}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function MiniSiteTrust({ theme, enabled }) {
  if (!enabled) return null;
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="ms-trust">
      <div className="rounded-lg p-5 text-white" style={{ background: theme }}>
        <div className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest">
          <ShieldCheck className="w-4 h-4" /> This business uses ConfirmPH
        </div>
        <p className="mt-2 text-sm opacity-90">
          This rental business uses ConfirmPH to help manage booking confirmations, reminders,
          rental records, and customer communication more clearly.
        </p>
      </div>
    </section>
  );
}

// Named exports for re-use by the multi-page mini-site (Enterprise).
export {
  PublicVehicleCard,
  ContactCard,
  PickupCard,
  BookingStatusCard,
  BusinessLocationSection,
  ReviewsSection,
  InquirySection,
  AboutGallery,
  MiniSiteHero,
  MiniSiteAbout,
  MiniSiteHowItWorks,
  MiniSitePolicies,
  MiniSiteFAQ,
  MiniSiteTrust,
};
